import * as React from 'react';
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from 'src/shadcn/components/ui/command';
import { gql } from '@apollo/client';
import { useHotToast } from 'src/utils/useHotToast';
import { useSearchCustomersForCustomerSearchLazyQuery } from './__generated__/SelectExistingCustomerPopover.generated';
import { SearchCustomerObject } from 'src/graphql/__generated__/globalTypes';
import { notEmpty } from 'src/utils/utils';
import { User2Icon } from 'lucide-react';
import { useSearchHistory } from 'src/utils/useSearchHistory';
import { SearchHistory } from 'src/Components/Shared/SearchHistory';
import { useDebouncedCallback } from 'use-debounce';

export interface ISelectExistingCustomerPopoverProps {
  open: boolean;
  setOpen: (newOpen: boolean) => void;
  onSelectCustomer: (
    customer: Omit<SearchCustomerObject, '__typename'>
  ) => void;
}

export const SelectExistingCustomerPopover: React.FunctionComponent<
  ISelectExistingCustomerPopoverProps
> = props => {
  const [searchTerm, setSearchTerm] = React.useState<string | undefined>('');
  const { searchResult, onSearch } = useSearchUsingTerm();
  const { history, addSearchEntry, removeSearchEntry, clearHistory } =
    useSearchHistory({ searchType: 'customer' });

  const debounced = useDebouncedCallback((val: string) => {
    addSearchEntry(val);
  }, 2000);

  return (
    <CommandDialog
      open={props.open}
      onOpenChange={newOpen => {
        props.setOpen(newOpen);
        if (!newOpen) {
          document.body.style.pointerEvents = 'auto';
        }
      }}
    >
      <CommandInput
        placeholder='Search customer by phone number...'
        value={searchTerm}
        onValueChange={newValue => {
          setSearchTerm(newValue);
          if (newValue.trim()) {
            onSearch(newValue);
            debounced(newValue);
          }
        }}
      />
      <CommandList>
        {!searchTerm || searchTerm.trim() === '' ? (
          <SearchHistory
            history={history}
            onSelectHistoryItem={term => {
              setSearchTerm(term);
              onSearch(term);
            }}
            onRemoveHistoryItem={removeSearchEntry}
            onClearHistory={clearHistory}
            emptyMessage='No recent customer searches'
            maxItems={3}
          />
        ) : searchResult?.customers && searchResult.customers.length > 0 ? (
          <CommandGroup heading=''>
            {searchResult?.customers.map(customer => (
              <CommandItem
                key={customer.id}
                value={customer.phoneNumber + customer.id}
                onSelect={currentValue => {
                  props.onSelectCustomer(customer);
                  setSearchTerm('');
                  props.setOpen(false);
                }}
              >
                <div className='flex flex-col'>
                  <div className='flex items-center'>
                    <User2Icon className='mr-2 h-3 w-3 min-w-3' />
                    {[customer.firstName, customer.lastName]
                      .filter(notEmpty)
                      .join(' ')}
                  </div>
                  {customer.linkedCustomers &&
                    customer.linkedCustomers.length > 0 && (
                      <div className='ml-1 mt-3 flex flex-col border-l-4 pl-3'>
                        <span className='text-xs font-semibold'>
                          Linked customer accounts
                        </span>
                        <div className='mt-2 flex flex-col gap-1'>
                          {customer.linkedCustomers?.map(linkedCustomer => (
                            <div key={linkedCustomer.id}>
                              {[
                                linkedCustomer.firstName,
                                linkedCustomer.lastName,
                              ]
                                .filter(notEmpty)
                                .join(' ')}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                </div>
              </CommandItem>
            ))}
          </CommandGroup>
        ) : (
          <CommandEmpty>Write a 10 digit phone number to search.</CommandEmpty>
        )}
      </CommandList>
    </CommandDialog>
  );
};

function useSearchUsingTerm() {
  const toast = useHotToast();
  const [onSearch, { loading: isLoadingCustomerSearch, data }] =
    useSearchCustomersForCustomerSearchLazyQuery({
      fetchPolicy: 'network-only',
      notifyOnNetworkStatusChange: true,
      onError: e => {
        toast.error(e.message);
      },
    });
  return {
    isLoadingCustomerSearch,
    searchResult: data?.searchCustomersBySearchTerm,
    onSearch: (searchTerm: string) => {
      onSearch({ variables: { searchTerm } });
    },
  };
}

const SEARCH_CUSTOMERS_QUERY = gql`
  query SearchCustomersForCustomerSearch($searchTerm: String!) {
    searchCustomersBySearchTerm(searchTerm: $searchTerm) {
      customers {
        createdAt
        id
        firstName
        lastName
        email
        phoneNumber
        secondaryPhoneNumber
        companyName
        linkedCustomers {
          id
          createdAt
          firstName
          lastName
          email
          phoneNumber
          secondaryPhoneNumber
          companyName
        }
      }
    }
  }
`;
